import React, { useState } from "react";
import styled from "styled-components";
import {
  useGetOrdersQuery,
  useCreateAppealMutation,
  useUploadAppealFilesMutation,
} from "../store/api";
import { useNavigate } from "react-router-dom";

const OrdersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const Subtitle = styled.p`
  color: var(--text-muted);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

// Фильтры
const FiltersContainer = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  border: 1px solid var(--border-divider);
  padding: 1.5rem;
`;

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const FiltersTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ClearFilters = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.875rem;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--text-primary);
  }
`;

const FilterTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const FilterTag = styled.div<{
  isActive: boolean;
  isExpanded?: boolean;
  hasContent?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border-divider);
  position: relative;
  min-width: ${(props) =>
    props.isExpanded || props.hasContent ? "auto" : "120px"};
  width: ${(props) =>
    props.isExpanded || props.hasContent ? "auto" : "120px"};
  max-width: 280px;
  height: 40px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const DateFilterTag = styled.div<{
  isActive: boolean;
  isExpanded?: boolean;
  hasContent?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border-divider);
  position: relative;
  min-width: ${(props) =>
    props.isExpanded || props.hasContent ? "340px" : "120px"};
  width: ${(props) =>
    props.isExpanded || props.hasContent ? "340px" : "120px"};
  height: 40px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  box-sizing: border-box;
  flex-shrink: 0;
`;

const FilterContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;

const FilterInput = styled.input`
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  padding: 0.125rem 0.375rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 80px;
  height: 28px;
  max-width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    background: transparent;
  }
`;

const FilterSelect = styled.select`
  background: transparent;
  border: none;
  border-radius: 0.375rem;
  padding: 0.125rem 0.375rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  min-width: 100px;
  height: 28px;
  max-width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;

  &:focus {
    outline: none;
    background: transparent;
  }

  option {
    background: var(--bg-primary);
    color: var(--text-primary);
  }
`;

const FilterValue = styled.span`
  margin-left: auto;
  margin-right: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
`;

const CloseIcon = styled.span`
  margin-left: auto;
  cursor: pointer;
  font-size: 1rem;
  opacity: 0.6;
  color: var(--text-muted);
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    color: var(--text-primary);
  }
`;

const AddFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  height: 40px;
  background: var(--bg-primary);
  color: var(--text-muted);
  border: 1px dashed #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-sizing: border-box;
  flex-shrink: 0;

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--text-muted);
    color: var(--text-primary);
  }
`;

// Таблица
const OrdersCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  border: 1px solid var(--border-divider);
  overflow: hidden;
`;

const TableHeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-divider);
  background: var(--bg-primary);
`;

const TableTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const TableRefreshButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-muted);

  &:hover:not(:disabled) {
    background: var(--bg-hover);
    color: var(--text-primary);
    border-color: var(--text-muted);
  }

  &:disabled {
    background: var(--bg-primary);
    color: #d1d5db;
    cursor: not-allowed;
  }
`;

const RefreshSvgIcon = styled.svg<{ spinning?: boolean }>`
  width: 1rem;
  height: 1rem;
  animation: ${(props) =>
    props.spinning ? "spin 1s linear infinite" : "none"};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: var(--bg-primary);
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-divider);
`;

const TableRow = styled.tr`
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background: var(--bg-primary);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-light);
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  ${({ status }) => {
    switch (status) {
      case "PENDING":
        return "background: var(--status-pending-bg); color: var(--status-pending-text);";
      case "PROCESSING": // Обратная совместимость
        return "background: var(--status-processing-bg); color: var(--status-processing-text);";
      case "COMPLETED":
        return "background: var(--status-success-bg); color: var(--status-success-text);";
      case "CANCELLED":
        return "background: var(--status-error-bg); color: var(--status-error-text);";
      case "FAILED":
        return "background: var(--status-error-bg); color: var(--status-error-text);";
      case "EXPIRED":
        return "background: var(--bg-secondary); color: var(--text-primary);";
      default:
        return "background: var(--bg-secondary); color: var(--text-primary);";
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
  font-size: 0.875rem;
`;

// Appeal Button
const AppealButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-muted);
  font-size: 0.875rem;

  &:hover:not(:disabled) {
    background: var(--bg-hover);
    color: var(--text-primary);
    border-color: var(--text-muted);
  }

  &:disabled {
    background: var(--bg-primary);
    color: #d1d5db;
    cursor: not-allowed;
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--border-input-focus);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    background: var(--bg-input-disabled);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: var(--border-input-focus);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileButton = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-hover);
  }
`;

const FilesInfo = styled.div`
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const HelperText = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
`;

const ErrorText = styled.div`
  font-size: 0.75rem;
  color: var(--accent-error);
  margin-top: 0.25rem;
`;

const FormActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const CancelButton = styled.button`
  background: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-input);
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--bg-primary);
  }
`;

const SubmitButton = styled.button`
  background: #b2b2b2;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #cecece;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

interface FilterState {
  status: string;
  search: string;
  bank: string;
  amountMin: string;
  amountMax: string;
  usdtMin: string;
  usdtMax: string;
  dateFrom: string;
  dateTo: string;
}

function Orders() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>({
    status: "",
    search: "",
    bank: "",
    amountMin: "",
    amountMax: "",
    usdtMin: "",
    usdtMax: "",
    dateFrom: "",
    dateTo: "",
  });

  const [tempFilters, setTempFilters] = useState<FilterState>(filters);
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);

  // Appeal modal state
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [appealReason, setAppealReason] = useState("");
  const [appealComment, setAppealComment] = useState("");
  const [appealFiles, setAppealFiles] = useState<File[]>([]);
  const [appealFormError, setAppealFormError] = useState<string>("");

  // Appeal mutations
  const [uploadFiles, { isLoading: isUploading }] =
    useUploadAppealFilesMutation();
  const [createAppeal, { isLoading: isCreating }] = useCreateAppealMutation();

  const {
    data: ordersResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetOrdersQuery({
    page: 1,
    limit: 50,
    status: filters.status || undefined,
    search: filters.search || undefined,
    bank: filters.bank || undefined,
    amountMin: filters.amountMin ? Number(filters.amountMin) : undefined,
    amountMax: filters.amountMax ? Number(filters.amountMax) : undefined,
    usdtMin: filters.usdtMin ? Number(filters.usdtMin) : undefined,
    usdtMax: filters.usdtMax ? Number(filters.usdtMax) : undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
  });

  const orders = ordersResponse?.data || [];
  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatUSDT = (amount: number) => {
    return `${amount.toFixed(2)} USDT`;
  };

  const formatRUB = (amount: number) => {
    return `${amount.toFixed(2)} ₽`;
  };

  const formatExchangeRate = (rate: number) => {
    return `${rate.toFixed(2)} ₽/USDT`;
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "PENDING":
        return "Ожидает";
      case "PROCESSING": // Обратная совместимость
        return "Ожидание";
      case "COMPLETED":
        return "Завершен";
      case "CANCELLED":
        return "Отменен";
      case "FAILED":
        return "Ошибка";
      case "EXPIRED":
        return "Истек";
      default:
        return status;
    }
  };

  const handleOrderClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const handleFilterApply = () => {
    setFilters(tempFilters);
    setExpandedFilter(null);
  };

  const handleFilterBlur = () => {
    // Небольшая задержка чтобы пользователь мог переключиться между полями внутри фильтра
    setTimeout(() => {
      if (
        expandedFilter &&
        !document.activeElement?.closest(`[data-filter="${expandedFilter}"]`)
      ) {
        handleFilterApply();
      }
    }, 100);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleFilterApply();
    }
    if (e.key === "Escape") {
      setTempFilters(filters);
      setExpandedFilter(null);
    }
  };

  const clearAllFilters = () => {
    const emptyFilters = {
      status: "",
      search: "",
      bank: "",
      amountMin: "",
      amountMax: "",
      usdtMin: "",
      usdtMax: "",
      dateFrom: "",
      dateTo: "",
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setExpandedFilter(null);
  };

  const removeFilter = (filterKey: keyof FilterState) => {
    const newFilters = { ...filters, [filterKey]: "" };
    setFilters(newFilters);
    setTempFilters(newFilters);
  };

  const toggleFilterExpansion = (filterType: string) => {
    if (expandedFilter === filterType) {
      setExpandedFilter(null);
    } else {
      setTempFilters(filters); // Reset temp filters when opening
      setExpandedFilter(filterType);
    }
  };

  const getFilterLabel = (key: keyof FilterState, value: string) => {
    switch (key) {
      case "status":
        return `Статус: ${getStatusText(value)}`;
      case "search":
        return `Поиск: ${value}`;
      case "bank":
        return `Банк: ${value}`;
      case "amountMin":
        return `От: ${value}₽`;
      case "amountMax":
        return `До: ${value}₽`;
      case "usdtMin":
        return `USDT от: ${value}`;
      case "usdtMax":
        return `USDT до: ${value}`;
      case "dateFrom":
        return `С: ${new Date(value).toLocaleDateString("ru-RU")}`;
      case "dateTo":
        return `По: ${new Date(value).toLocaleDateString("ru-RU")}`;
      default:
        return value;
    }
  };

  // Appeal functions
  const handleCreateAppeal = (orderId: string) => {
    setSelectedOrderId(orderId);
    setShowAppealModal(true);
  };

  const handleAppealFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAppealFiles(Array.from(e.target.files));
    }
  };

  const resetAppealForm = () => {
    setSelectedOrderId("");
    setAppealReason("");
    setAppealComment("");
    setAppealFiles([]);
    setAppealFormError("");
  };

  const handleAppealSubmit = async () => {
    setAppealFormError("");

    if (!selectedOrderId) {
      setAppealFormError("Ордер не выбран");
      return;
    }

    if (appealFiles.length === 0) {
      setAppealFormError("Прикрепите хотя бы один файл");
      return;
    }

    try {
      const uploadRes = await uploadFiles(appealFiles).unwrap();
      const uploaded = uploadRes.files || [];
      const images = uploaded.filter((f) => f.type === "image");
      const videos = uploaded.filter((f) => f.type === "video");
      const documents = uploaded.filter((f) => f.type === "document");

      await createAppeal({
        orderId: selectedOrderId,
        reason: appealReason || undefined,
        comment: appealComment || undefined,
        images,
        videos,
        documents,
      }).unwrap();

      resetAppealForm();
      setShowAppealModal(false);
    } catch (error) {
      setAppealFormError("Ошибка при создании апелляции");
    }
  };

  return (
    <OrdersContainer>
      <Header>
        <HeaderLeft>
          <Subtitle>Управление ордерами и их статусами</Subtitle>
        </HeaderLeft>
      </Header>

      <FiltersContainer>
        <FiltersHeader>
          <FiltersTitle>
            Фильтры {activeFiltersCount > 0 && `(${activeFiltersCount})`}
          </FiltersTitle>
          {activeFiltersCount > 0 && (
            <ClearFilters onClick={clearAllFilters}>Очистить все</ClearFilters>
          )}
        </FiltersHeader>

        <FilterTags>
          {/* Статус фильтр */}
          {filters.status || expandedFilter === "status" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "status"}
              hasContent={!!filters.status}
              onClick={() => toggleFilterExpansion("status")}
              data-filter="status"
            >
              <FilterContent>
                <span>Статус:</span>
                {expandedFilter === "status" ? (
                  <FilterSelect
                    value={tempFilters.status}
                    onChange={(e) => {
                      setTempFilters({
                        ...tempFilters,
                        status: e.target.value,
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={handleFilterBlur}
                    onKeyDown={handleKeyPress}
                    autoFocus
                  >
                    <option value="">Все статусы</option>
                    <option value="PENDING">Ожидает</option>
                    <option value="COMPLETED">Завершен</option>
                    <option value="CANCELLED">Отменен</option>
                    <option value="FAILED">Ошибка</option>
                    <option value="EXPIRED">Истек</option>
                  </FilterSelect>
                ) : (
                  <FilterValue>
                    {getFilterLabel("status", filters.status)}
                  </FilterValue>
                )}
                <CloseIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFilter("status");
                  }}
                >
                  ×
                </CloseIcon>
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("status")}>
              + Статус
            </AddFilterButton>
          )}

          {/* Поиск фильтр */}
          {filters.search || expandedFilter === "search" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "search"}
              hasContent={!!filters.search}
              onClick={() => toggleFilterExpansion("search")}
              data-filter="search"
            >
              <FilterContent>
                <span>Поиск:</span>
                {expandedFilter === "search" ? (
                  <FilterInput
                    type="text"
                    placeholder="ORDER_123"
                    value={tempFilters.search}
                    onChange={(e) =>
                      setTempFilters({ ...tempFilters, search: e.target.value })
                    }
                    onClick={(e) => e.stopPropagation()}
                    onBlur={handleFilterBlur}
                    onKeyDown={handleKeyPress}
                    style={{
                      marginLeft: "auto",
                      width: "120px",
                    }}
                    autoFocus
                  />
                ) : (
                  <FilterValue>{filters.search}</FilterValue>
                )}
                <CloseIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFilter("search");
                  }}
                >
                  ×
                </CloseIcon>
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("search")}>
              + Поиск
            </AddFilterButton>
          )}

          {/* Банк фильтр */}
          {filters.bank || expandedFilter === "bank" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "bank"}
              hasContent={!!filters.bank}
              onClick={() => toggleFilterExpansion("bank")}
              data-filter="bank"
            >
              <FilterContent>
                <span>Банк:</span>
                {expandedFilter === "bank" ? (
                  <FilterInput
                    type="text"
                    placeholder="sberbank, sbp"
                    value={tempFilters.bank}
                    onChange={(e) =>
                      setTempFilters({ ...tempFilters, bank: e.target.value })
                    }
                    onClick={(e) => e.stopPropagation()}
                    onBlur={handleFilterBlur}
                    onKeyDown={handleKeyPress}
                    style={{
                      marginLeft: "auto",
                      width: "120px",
                    }}
                    autoFocus
                  />
                ) : (
                  <FilterValue>{filters.bank}</FilterValue>
                )}
                <CloseIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFilter("bank");
                  }}
                >
                  ×
                </CloseIcon>
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("bank")}>
              + Банк
            </AddFilterButton>
          )}

          {/* Сумма RUB фильтр */}
          {filters.amountMin ||
          filters.amountMax ||
          expandedFilter === "amount" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "amount"}
              hasContent={!!(filters.amountMin || filters.amountMax)}
              onClick={() => toggleFilterExpansion("amount")}
              data-filter="amount"
            >
              <FilterContent>
                <span>Сумма (RUB):</span>
                {expandedFilter === "amount" ? (
                  <div
                    style={{
                      marginLeft: "auto",
                      marginRight: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FilterInput
                      type="number"
                      placeholder="От"
                      value={tempFilters.amountMin}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          amountMin: e.target.value,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleFilterBlur}
                      onKeyDown={handleKeyPress}
                      style={{ width: "60px" }}
                      autoFocus
                    />
                    <span style={{ color: "#9ca3af" }}>—</span>
                    <FilterInput
                      type="number"
                      placeholder="До"
                      value={tempFilters.amountMax}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          amountMax: e.target.value,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleFilterBlur}
                      onKeyDown={handleKeyPress}
                      style={{ width: "60px" }}
                    />
                  </div>
                ) : (
                  <FilterValue>
                    {filters.amountMin && `от ${filters.amountMin}₽`}{" "}
                    {filters.amountMax && `до ${filters.amountMax}₽`}
                  </FilterValue>
                )}
                <CloseIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFilter("amountMin");
                    removeFilter("amountMax");
                  }}
                >
                  ×
                </CloseIcon>
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("amount")}>
              + Сумма (RUB)
            </AddFilterButton>
          )}

          {/* Сумма USDT фильтр */}
          {filters.usdtMin || filters.usdtMax || expandedFilter === "usdt" ? (
            <FilterTag
              isActive={true}
              isExpanded={expandedFilter === "usdt"}
              hasContent={!!(filters.usdtMin || filters.usdtMax)}
              onClick={() => toggleFilterExpansion("usdt")}
              data-filter="usdt"
            >
              <FilterContent>
                <span>Сумма (USDT):</span>
                {expandedFilter === "usdt" ? (
                  <div
                    style={{
                      marginLeft: "auto",
                      marginRight: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FilterInput
                      type="number"
                      placeholder="От"
                      value={tempFilters.usdtMin}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          usdtMin: e.target.value,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleFilterBlur}
                      onKeyDown={handleKeyPress}
                      style={{ width: "60px" }}
                      autoFocus
                    />
                    <span style={{ color: "#9ca3af" }}>—</span>
                    <FilterInput
                      type="number"
                      placeholder="До"
                      value={tempFilters.usdtMax}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          usdtMax: e.target.value,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleFilterBlur}
                      onKeyDown={handleKeyPress}
                      style={{ width: "60px" }}
                    />
                  </div>
                ) : (
                  <FilterValue>
                    {filters.usdtMin && `от ${filters.usdtMin}`}{" "}
                    {filters.usdtMax && `до ${filters.usdtMax}`}
                  </FilterValue>
                )}
                <CloseIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFilter("usdtMin");
                    removeFilter("usdtMax");
                  }}
                >
                  ×
                </CloseIcon>
              </FilterContent>
            </FilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("usdt")}>
              + Сумма (USDT)
            </AddFilterButton>
          )}

          {/* Дата фильтр */}
          {filters.dateFrom || filters.dateTo || expandedFilter === "date" ? (
            <DateFilterTag
              isActive={true}
              isExpanded={expandedFilter === "date"}
              hasContent={!!(filters.dateFrom || filters.dateTo)}
              onClick={() => toggleFilterExpansion("date")}
              data-filter="date"
            >
              <FilterContent>
                <span>Период:</span>
                {expandedFilter === "date" ? (
                  <div
                    style={{
                      marginLeft: "auto",
                      marginRight: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FilterInput
                      type="date"
                      value={tempFilters.dateFrom}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          dateFrom: e.target.value,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleFilterBlur}
                      onKeyDown={handleKeyPress}
                      style={{ width: "110px" }}
                      autoFocus
                    />
                    <span style={{ color: "#9ca3af" }}>—</span>
                    <FilterInput
                      type="date"
                      value={tempFilters.dateTo}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          dateTo: e.target.value,
                        })
                      }
                      onClick={(e) => e.stopPropagation()}
                      onBlur={handleFilterBlur}
                      onKeyDown={handleKeyPress}
                      style={{ width: "110px" }}
                    />
                  </div>
                ) : (
                  <FilterValue>
                    {filters.dateFrom &&
                      `С: ${new Date(filters.dateFrom).toLocaleDateString("ru-RU")}`}{" "}
                    {filters.dateTo &&
                      `По: ${new Date(filters.dateTo).toLocaleDateString("ru-RU")}`}
                  </FilterValue>
                )}
                <CloseIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFilter("dateFrom");
                    removeFilter("dateTo");
                  }}
                >
                  ×
                </CloseIcon>
              </FilterContent>
            </DateFilterTag>
          ) : (
            <AddFilterButton onClick={() => toggleFilterExpansion("date")}>
              + Дата
            </AddFilterButton>
          )}
        </FilterTags>
      </FiltersContainer>

      <OrdersCard>
        <TableHeaderSection>
          <TableTitle>Список ордеров</TableTitle>
          <TableRefreshButton onClick={() => refetch()} disabled={isFetching}>
            <RefreshSvgIcon
              spinning={isFetching}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </RefreshSvgIcon>
          </TableRefreshButton>
        </TableHeaderSection>
        <TableScroll>
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell>#ID</TableHeaderCell>
              <TableHeaderCell>ID_merch</TableHeaderCell>
              <TableHeaderCell>ID_client</TableHeaderCell>
              <TableHeaderCell>Дата создания</TableHeaderCell>
              <TableHeaderCell>Дата оплаты</TableHeaderCell>
              <TableHeaderCell>Метод</TableHeaderCell>
              <TableHeaderCell>Сумма</TableHeaderCell>
              <TableHeaderCell>Курс</TableHeaderCell>
              <TableHeaderCell>Сумма (USDT)</TableHeaderCell>
              <TableHeaderCell>Статус</TableHeaderCell>
              <TableHeaderCell
                style={{ textAlign: "center" }}
              ></TableHeaderCell>
            </tr>
          </TableHeader>
          <tbody>
            {isLoading ? (
              <tr>
                <TableCell colSpan={11}>
                  <EmptyState>Загрузка...</EmptyState>
                </TableCell>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <TableCell colSpan={11}>
                  <EmptyState>Нет ордеров для отображения</EmptyState>
                </TableCell>
              </tr>
            ) : (
              orders.map((order: any) => (
                <TableRow
                  key={order.id}
                  onClick={() => handleOrderClick(order.id)}
                >
                  <TableCell>{order.id}</TableCell>
                  <TableCell>{order.invId || "—"}</TableCell>
                  <TableCell>{order.clientId || "—"}</TableCell>
                  <TableCell>{formatDate(order.createdAt)}</TableCell>
                  <TableCell>
                    {order.paidAt ? formatDate(order.paidAt) : "—"}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const method = order.method ? order.method.toUpperCase() : null;
                      if (method === "SBP") return "СБП";
                      if (method === "CARD_NUMBER" || method === "CARD") return "Карта";
                      if (method === "ACCOUNT_TRANSFER" || method === "ACCOUNT") return "Счет";
                      return order.bank || method || "—";
                    })()}
                  </TableCell>
                  <TableCell>
                    {order.currency === "RUB"
                      ? formatRUB(order.amount)
                      : formatUSDT(order.amount)}
                  </TableCell>
                  <TableCell>
                    {order.exchangeRate
                      ? formatExchangeRate(order.exchangeRate)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {formatUSDT(
                      order.usdtAmount ||
                        order.amount / (order.exchangeRate || 80.36)
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={order.status}>
                      {getStatusText(order.status)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell style={{ textAlign: "center" }}>
                    <AppealButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreateAppeal(order.id);
                      }}
                      title="Создать апелляцию"
                    >
                      ⚠️
                    </AppealButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </tbody>
        </Table>
        </TableScroll>
      </OrdersCard>

      {/* Appeal Modal */}
      {showAppealModal && (
        <ModalOverlay onClick={() => setShowAppealModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Создать апелляцию</ModalTitle>
              <CloseButton onClick={() => setShowAppealModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <FormGroup>
              <Label>ID Ордера</Label>
              <Input
                type="text"
                value={selectedOrderId}
                disabled
              />
            </FormGroup>

            <FormGroup>
              <Label>Причина</Label>
              <Input
                type="text"
                placeholder="Опишите причину апелляции"
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Описание</Label>
              <Textarea
                placeholder="Дополнительная информация о проблеме"
                value={appealComment}
                onChange={(e) => setAppealComment(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Файлы (PDF, видео, изображения) — обязательно *</Label>
              <FileInput
                id="appeal-files"
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx"
                onChange={handleAppealFileChange}
              />
              <FileButton htmlFor="appeal-files">Выбрать файлы</FileButton>
              {appealFiles.length > 0 && (
                <FilesInfo>Выбрано файлов: {appealFiles.length}</FilesInfo>
              )}
              <HelperText>
                Прикрепите скриншоты/видео/документы, подтверждающие проблему.
              </HelperText>
              {appealFiles.length === 0 && appealFormError && (
                <ErrorText>Прикрепите хотя бы один файл</ErrorText>
              )}
            </FormGroup>

            {appealFormError && <ErrorText>{appealFormError}</ErrorText>}

            <FormActions>
              <CancelButton
                onClick={() => {
                  resetAppealForm();
                  setShowAppealModal(false);
                }}
              >
                Отмена
              </CancelButton>
              <SubmitButton
                onClick={handleAppealSubmit}
                disabled={isUploading || isCreating}
              >
                {isUploading || isCreating
                  ? "Создание..."
                  : "Создать апелляцию"}
              </SubmitButton>
            </FormActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </OrdersContainer>
  );
}

export default Orders;
